import {
  Autocomplete as HeroAutocomplete,
  AutocompleteItem as HeroAutocompleteItem,
  AutocompleteProps as HeroAutocompleteProps,
} from "@heroui/react";
import clsx from "clsx";
import { WithId } from "core/types";
import { ReactNode } from "react";
import { getInputClassNames } from "../../utils/styles";

export type AutocompleteProps<T extends WithId = WithId> = Omit<
  HeroAutocompleteProps<T>,
  "children"
> & {
  // HeroUI expects non string items to provide an textValue property for accessibility reasons.
  renderItem: (item: T) => { textValue: string; node: ReactNode } | string;
};

export function Autocomplete<T extends WithId = WithId>({
  renderItem,
  ...props
}: AutocompleteProps<T>) {
  return (
    <HeroAutocomplete
      {...props}
      classNames={{
        base: "opacity-100!",
        clearButton: [
          "text-medium",
          "translate-x-1",
          "cursor-text",
          "opacity-0",
          "pointer-events-none",
          "text-default-900",
          "group-data-[invalid=true]:text-danger",
          "data-[visible=true]:opacity-100",
          "data-[visible=true]:pointer-events-auto",
          "data-[visible=true]:cursor-pointer",
        ],
        endContentWrapper: [
          "flex h-full items-center -mr-2",
          clsx({
            "[&>svg]:w-4": props.size === "sm",
            "[&>svg]:w-5": props.size === "md",
            "[&>svg]:w-6": props.size === "lg",
          }),
          "[&>svg]:stroke-sm!",
        ],
        listboxWrapper: "scroll-py-6 max-h-64",
        popoverContent: "p-1 overflow-hidden",
        selectorButton: [
          "text-medium text-default-900",
          "group-data-[invalid=true]:text-danger",
        ],
        ...(props.classNames ?? {}),
      }}
      color="primary"
      inputProps={{
        ...props.inputProps,
        classNames: getInputClassNames(props.size),
      }}
      labelPlacement="outside"
      variant="bordered"
    >
      {(item) => {
        const renderedItem = renderItem(item);
        const textValue =
          typeof renderedItem === "string"
            ? renderedItem
            : renderedItem.textValue;
        const node =
          typeof renderedItem === "string" ? renderedItem : renderedItem.node;

        return (
          <HeroAutocompleteItem
            key={String(item.id)}
            className={clsx(
              "group rounded-lg bg-white! p-2",
              "[&>svg]:w-4!",

              // Ring on focus and hover
              "data-[focus=true]:ring-1! data-[hover=true]:ring-1!",

              // Outline on focus and hover
              "data-[focus=true]:outline-1! data-[focus=true]:outline-offset-1! data-[focus=true]:outline-solid!",
              "data-[hover=true]:outline-1! data-[hover=true]:outline-offset-1! data-[hover=true]:outline-solid!",

              // Background color on focus and hover
              "data-[focus=true]:bg-primary-light! data-[hover=true]:bg-primary-light!",

              // Text color on focus and hover
              "data-[focus=true]:text-primary-light-foreground! data-[hover=true]:text-primary-light-foreground!",

              // Ring color
              "data-[focus=true]:ring-primary data-[hover=true]:ring-primary",
            )}
            textValue={textValue}
          >
            {node}
          </HeroAutocompleteItem>
        );
      }}
    </HeroAutocomplete>
  );
}

Autocomplete.displayName = "Recare.Autocomplete";
